import type { Dossier } from '../../lib/useApprenants';
import type { DossierEntretien, LigneEntretien } from '../../lib/useEntretiens';

/** `AAAA-MM-JJ` attendu par la fiche, depuis l'ISO de l'API. */
function jourSaisi(valeur: string | null): string {
  return valeur ? valeur.slice(0, 10) : '';
}

/**
 * Ce que le dossier sait déjà du candidat, sous la forme que la fiche
 * d'entretien attend pour pré-remplir ses rubriques vides. Le dossier est la
 * source : la fiche ne redemande pas ce qui a déjà été saisi (CDC §4.1).
 */
function dossierPourLaFiche(dossier: Dossier): DossierEntretien {
  const representant = dossier.cerfa?.representant;
  const responsableLegal = [
    [representant?.prenom, representant?.nom].filter(Boolean).join(' '),
    representant?.courriel,
  ]
    .filter((partie) => partie && partie.trim() !== '')
    .join(' · ');

  return {
    mode: dossier.dossier.mode,
    formationCode: dossier.dossier.formation?.code ?? null,
    mineur: dossier.situation.mineur,
    identite: {
      nomNaissance: dossier.identite.nom,
      prenoms: dossier.identite.prenom,
      nomUsage: dossier.cerfa?.nomUsage ?? '',
      dateNaissance: jourSaisi(dossier.identite.dateNaissance),
      communeNaissance: dossier.identite.lieuNaissance ?? '',
      nationalite: dossier.identite.nationalite ?? '',
      telephone: dossier.identite.telephone ?? '',
      mail: dossier.identite.email ?? '',
      adresse: dossier.identite.adresse ?? '',
      codePostal: dossier.identite.codePostal ?? '',
      ville: dossier.identite.ville ?? '',
      // Le dossier ne porte pas de numéro de pièce d'identité : la rubrique
      // reste à la saisie de l'entretien.
      pieceIdentite: '',
    },
    responsableLegal,
  };
}

/**
 * Entretien de recrutement du dossier, sous la forme du tableau de suivi, pour
 * rouvrir la fiche depuis l'écran « Dossier apprenant » avec le même
 * formulaire que le pôle commercial — un seul écran de saisie, une seule
 * source.
 *
 * `clos` est toujours vrai ici : depuis le dossier, la fiche se corrige, mais
 * l'issue de l'entretien — retenu, non retenu, à revoir — se prend depuis le
 * tableau des entretiens, dont l'API est fermée au secrétariat.
 */
export function ligneEntretienDuDossier(dossier: Dossier): LigneEntretien | null {
  const entretien = dossier.entretien;
  if (!entretien) return null;

  return {
    id: entretien.id,
    type: 'CANDIDAT',
    interlocuteur: `${dossier.identite.prenom} ${dossier.identite.nom}`,
    apprenantId: dossier.identite.id,
    entrepriseId: null,
    formation: dossier.dossier.formation?.titre ?? null,
    objet: null,
    dateEntretien: entretien.dateEntretien,
    charge: null,
    etat: 'REALISE',
    etatLibelle: 'Réalisé',
    etatTon: 'info',
    compteRendu: entretien.compteRendu,
    besoinsRecueillis: entretien.besoinsRecueillis,
    fiche: entretien.fiche,
    orientation: entretien.orientation,
    dossier: dossierPourLaFiche(dossier),
    sites: [dossier.dossier.site.code],
    clos: true,
    modifieLe: entretien.dateEntretien,
  };
}
